import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import { useToast } from "../../components/Toast/toastHook";
import { GET_SUB_COMPANIES_BY_SERVICE } from "../../graphql/agentQueries";
import { Table, Button, Modal } from "antd";
import { EyeOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { formatDate } from "../../utils/dateFormat";
import TableToolbar from "../../components/TableToolbar/TableToolbar";
import useDebounce from "../../hooks/useDebounce";
import { toServerSort } from "../../utils/tableSort";
import { TABLE_SCROLL } from "../../constants/table";
import useIsPhone from "../../hooks/useIsPhone";
import CardList from "../../components/CardList/CardList";
import ListCard, { CardAction, initialsOf } from "../../components/CardList/ListCard";

// Columns the server can order the sub-companies by.
const SORTABLE_COLUMNS = ["name", "address", "phone", "website", "createdAt"];

const AgentSubCompanies = () => {
  const [subCompanies, setSubCompanies] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const debounceValue = useDebounce(searchText, 500);
  const [sort, setSort] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedSubCompany, setSelectedSubCompany] = useState(null);
  const { companyId, serviceId } = useParams();
  const location = useLocation();
  const serviceName = location.state?.serviceName || "Service";
  const navigate = useNavigate();
  const toast = useToast(4000);
  const isPhone = useIsPhone();

  const { loading, error, data, refetch } = useQuery(GET_SUB_COMPANIES_BY_SERVICE, {
    variables: {
      serviceId,
      pagination,
      ...(debounceValue && { searchText: debounceValue }),
      ...(sort && { sort }),
    },
    fetchPolicy: "network-only",
    skip: !serviceId,
  });

  useEffect(() => {
    if (data?.getSubCompanyByServiceID) {
      setSubCompanies(data.getSubCompanyByServiceID.subCompanies || []);
      setTotalCount(data.getSubCompanyByServiceID.totalCount || 0);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      toast("error", error.message);
    }
  }, [error]);

  const handleTableChange = (paginationConfig, _tableFilters, sorter, extra) => {
    // A new sort order starts again from the first page.
    if (extra?.action === "sort") {
      setSort(toServerSort(sorter, SORTABLE_COLUMNS));
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    setPagination({
      page: paginationConfig.current,
      limit: paginationConfig.pageSize,
    });
  };

  // A new search starts again from the first page.
  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleViewContacts = (subCompany) => {
    navigate(
      `/company/${companyId}/agent/services/${serviceId}/sub-companies/${subCompany._id}/contacts`,
      {
        state: { serviceName, subCompanyName: subCompany.name },
      }
    );
  };

  const handleViewDetails = (subCompany) => {
    setSelectedSubCompany(subCompany);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedSubCompany(null);
  };

  const handleGoBack = () => {
    navigate(`/company/${companyId}/agent/services`);
  };

  const columns = [
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (name) => <span className="font-medium text-black">{name}</span>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      sorter: true,
      render: (address) => (
        <span className="text-gray-600 text-sm">
          {address ? (address.length > 50 ? `${address.substring(0, 50)}...` : address) : "-"}
        </span>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      sorter: true,
      render: (phone) => phone || "-",
    },
    {
      title: "Website",
      dataIndex: "website",
      key: "website",
      sorter: true,
      render: (website) =>
        website ? (
          <a
            href={website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Visit
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (date) => formatDate(date) || "-",
    },
    {
      title: "Actions",
      key: "actions",
      skeleton: "actions",
      width: 200,
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
            title="View Details"
          >
            Details
          </Button>
          <Button
            type="primary"
            className="bg-primary"
            onClick={() => handleViewContacts(record)}
            title="View Contacts"
          >
            Contacts
          </Button>
        </div>
      ),
    },
  ];

  const firstLoad = loading && subCompanies.length === 0;

  const emptyState = searchText ? (
    <EmptyState
      title="No sub-companies match your search"
      description={`Nothing found for "${searchText}".`}
      actionLabel="Clear search"
      onAction={() => handleSearch("")}
    />
  ) : (
    <EmptyState
      title="No sub-companies yet"
      description="Sub-companies under this service will appear here."
    />
  );

  const subCompanyCard = (subCompany) => (
    <ListCard
      title={subCompany.name}
      initials={initialsOf(subCompany.name)}
      subtitle={subCompany.address || "No address"}
      meta={`Created ${formatDate(subCompany.createdAt) || "-"}`}
      actions={
        <>
          <CardAction icon={<EyeOutlined />} onClick={() => handleViewDetails(subCompany)}>
            Details
          </CardAction>
          <CardAction
            type="primary"
            className="bg-primary"
            onClick={() => handleViewContacts(subCompany)}
          >
            Contacts
          </CardAction>
        </>
      }
    >
      {subCompany.phone ? (
        <a href={`tel:${subCompany.phone}`} className="text-primary_dark hover:underline">
          {subCompany.phone}
        </a>
      ) : (
        <span>No phone</span>
      )}
      {subCompany.website ? (
        <a
          href={subCompany.website}
          target="_blank"
          rel="noopener noreferrer"
          className="break-all text-primary_dark hover:underline"
        >
          Visit website
        </a>
      ) : (
        <span>No website</span>
      )}
    </ListCard>
  );

  return (
    <div id="agent-sub-companies">
      <Breadcrumb pageName={`${serviceName} - Sub Companies`} />
      <div className="pb-6">
        <Card>
          <div className="mx-4 mt-4">
            <Button icon={<ArrowLeftOutlined />} onClick={handleGoBack}>
              Back
            </Button>
          </div>
          <TableToolbar
            title="Sub Companies"
            count={totalCount}
            searchValue={searchText}
            onSearch={handleSearch}
          />
          {error && !subCompanies.length ? (
            <ErrorState onRetry={() => refetch()} />
          ) : (
            <>
              {error && <ErrorBanner onRetry={() => refetch()} />}
              {isPhone ? (
                <CardList
                  className="px-4 pb-4"
                  label="Sub Companies"
                  loading={loading}
                  items={subCompanies}
                  rowKey={(subCompany) => subCompany._id}
                  empty={emptyState}
                  renderItem={subCompanyCard}
                  pagination={{
                    current: pagination.page,
                    pageSize: pagination.limit,
                    total: totalCount,
                    onChange: (page) => setPagination((prev) => ({ ...prev, page })),
                  }}
                  columns={columns}
                  sort={
                    sort
                      ? { columnKey: sort.field, order: sort.order === "asc" ? "ascend" : "descend" }
                      : {}
                  }
                  onSortChange={(sorter) => {
                    setSort(toServerSort(sorter, SORTABLE_COLUMNS));
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                />
              ) : (
                <Table
                  scroll={TABLE_SCROLL}
                  bordered
                  rowKey="_id"
                  onChange={handleTableChange}
                  {...(firstLoad
                    ? skeletonTableProps(columns)
                    : {
                        columns,
                        dataSource: subCompanies,
                        loading,
                        pagination: {
                          position: ["bottomCenter"],
                          current: pagination.page,
                          pageSize: pagination.limit,
                          total: totalCount,
                          showSizeChanger: true,
                        },
                      })}
                  locale={{ emptyText: emptyState }}
                />
              )}
            </>
          )}
        </Card>
      </div>

      {/* View Sub Company Details Modal */}
      <Modal
        title={selectedSubCompany?.name}
        open={isViewModalOpen}
        onCancel={handleCloseViewModal}
        footer={null}
        width={600}
      >
        {selectedSubCompany && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-500 text-sm">Phone</p>
                <p className="font-medium">{selectedSubCompany.phone || "-"}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Website</p>
                {selectedSubCompany.website ? (
                  <a
                    href={selectedSubCompany.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {selectedSubCompany.website}
                  </a>
                ) : (
                  <p className="font-medium">-</p>
                )}
              </div>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Address</p>
              <p className="font-medium">{selectedSubCompany.address || "-"}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Service Areas</p>
              <p className="font-medium">{selectedSubCompany.serviceAreas || "-"}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Notes</p>
              <p className="font-medium">{selectedSubCompany.notes || "-"}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Created Date</p>
              <p className="font-medium">{formatDate(selectedSubCompany.createdAt) || "-"}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AgentSubCompanies;
